/**
 * Pure state machine for the Admin Panel two-step login (TOTP).
 *
 * Flow (spec §6):
 *   password → code         → done          (existing admin with TOTP enabled)
 *   password → enroll       → backup-codes → done  (first login: forced enrollment)
 *   password → done                          (ADMIN_TOTP_ENFORCED=false — legacy shape {status, token})
 *
 * API contract (spec §4) — all routes under /api/v1/admin, JSON:
 *   POST /login               {email, password}
 *     → {status:true, mfa_required:true, enrollment_required:bool, mfa_token}
 *     → {status:true, message, token}                     (enforcement off)
 *   POST /mfa/enroll/start    {mfa_token}       → {status:true, otpauth_url, qr_data_url, manual_key}
 *   POST /mfa/enroll/confirm  {mfa_token, code} → {status:true, token, backup_codes:[10]}
 *   POST /mfa/verify          {mfa_token, code} → {status:true, token}
 *   Errors: HTTP 400 / 429, body {status:false, message, code}
 *     codes: INVALID_CREDENTIALS, MFA_CODE_INVALID, MFA_LOCKED, MFA_TOKEN_EXPIRED, RATE_LIMITED
 *   These routes NEVER return 401 — a 400 here must never be treated as a logout.
 *
 * This module has no React / DOM / axios dependency so it can be unit-tested directly.
 * The session token is kept in state and written to localStorage by the component
 * only when `step === 'done'` (for enrollment: after the user acknowledges the backup codes).
 */

export type LoginStep = 'password' | 'code' | 'enroll' | 'backup-codes' | 'done'

export const MAX_MFA_ATTEMPTS = 5
export const MFA_LOCK_MINUTES = 15

export interface EnrollmentData {
  otpauthUrl: string
  qrDataUrl: string
  manualKey: string
}

export interface LoginFlowState {
  step: LoginStep
  /** Short-lived pre-auth token from POST /login (5 min). Only ever sent to /mfa/*. */
  mfaToken: string | null
  /** Session JWT. Held in memory until the component commits it to localStorage. */
  sessionToken: string | null
  enrollment: EnrollmentData | null
  backupCodes: string[]
  error: string | null
  errorCode: string | null
  /** Increments on every failed request — lets inputs reset even when the message repeats. */
  errorSeq: number
  /** Client-side count of MFA_CODE_INVALID responses in this sign-in attempt. */
  failedAttempts: number
}

export const initialLoginFlowState: LoginFlowState = {
  step: 'password',
  mfaToken: null,
  sessionToken: null,
  enrollment: null,
  backupCodes: [],
  error: null,
  errorCode: null,
  errorSeq: 0,
  failedAttempts: 0,
}

// ---------------------------------------------------------------------------
// API response shapes (spec §4)
// ---------------------------------------------------------------------------

export interface LoginApiResponse {
  status?: boolean
  message?: string
  token?: string
  mfa_required?: boolean
  enrollment_required?: boolean
  mfa_token?: string
}

export interface EnrollStartApiResponse {
  status?: boolean
  otpauth_url?: string
  qr_data_url?: string
  manual_key?: string
}

export interface EnrollConfirmApiResponse {
  status?: boolean
  token?: string
  backup_codes?: string[]
}

export interface VerifyApiResponse {
  status?: boolean
  token?: string
}

/** Normalised view of a failed request (axios error or anything else). */
export interface ApiFailure {
  httpStatus: number | null
  code: string | null
  message: string | null
}

export type LoginFlowAction =
  | {type: 'LOGIN_SUCCEEDED'; response: LoginApiResponse}
  | {type: 'ENROLL_STARTED'; response: EnrollStartApiResponse}
  | {type: 'ENROLL_CONFIRMED'; response: EnrollConfirmApiResponse}
  | {type: 'VERIFIED'; response: VerifyApiResponse}
  | {type: 'CODES_ACKNOWLEDGED'}
  | {type: 'REQUEST_FAILED'; failure: ApiFailure}
  | {type: 'BACK_TO_PASSWORD'}
  | {type: 'CLEAR_ERROR'}

// ---------------------------------------------------------------------------
// Error mapping
// ---------------------------------------------------------------------------

export const MESSAGES = {
  INVALID_CREDENTIALS: 'Invalid email or password.',
  MFA_TOKEN_EXPIRED: 'Your sign-in session has expired. Please enter your email and password again.',
  MFA_LOCKED: `Too many incorrect codes. Sign-in for this account is locked for ${MFA_LOCK_MINUTES} minutes. Please try again later.`,
  RATE_LIMITED: 'Too many attempts. Please wait a few minutes and try again.',
  NETWORK: 'Could not reach the server. Please check your connection and try again.',
  UNEXPECTED: 'Something went wrong. Please try again.',
  UNEXPECTED_RESPONSE: 'Unexpected response from the server. Please try again.',
} as const

export function mfaCodeInvalidMessage(failedAttempts: number): string {
  const used = Math.min(failedAttempts, MAX_MFA_ATTEMPTS)
  return `That code is not valid. Please try again. (${used} of ${MAX_MFA_ATTEMPTS} attempts used — after ${MAX_MFA_ATTEMPTS} incorrect codes the account is locked for ${MFA_LOCK_MINUTES} minutes.)`
}

/**
 * Turn whatever a rejected axios promise gives us into {httpStatus, code, message}.
 * Never throws.
 */
export function describeApiFailure(err: unknown): ApiFailure {
  const anyErr = err as {response?: {status?: unknown; data?: unknown}} | null | undefined
  const response = anyErr && typeof anyErr === 'object' ? anyErr.response : undefined
  if (!response || typeof response !== 'object') {
    return {httpStatus: null, code: null, message: null}
  }
  const httpStatus = typeof response.status === 'number' ? response.status : null
  const data = (response.data && typeof response.data === 'object' ? response.data : {}) as {
    code?: unknown
    message?: unknown
  }
  const code = typeof data.code === 'string' && data.code.trim() ? data.code.trim() : null
  const message = typeof data.message === 'string' && data.message.trim() ? data.message.trim() : null
  return {httpStatus, code, message}
}

/** Codes that invalidate the mfa_token and send the user back to the password step. */
const RETURN_TO_PASSWORD_CODES = new Set(['MFA_TOKEN_EXPIRED', 'MFA_LOCKED'])

// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------

function withError(state: LoginFlowState, error: string, errorCode: string | null): LoginFlowState {
  return {...state, error, errorCode, errorSeq: state.errorSeq + 1}
}

export function loginFlowReducer(state: LoginFlowState, action: LoginFlowAction): LoginFlowState {
  switch (action.type) {
    case 'LOGIN_SUCCEEDED': {
      const res = action.response || {}
      if (res.status !== true) {
        return withError(state, MESSAGES.INVALID_CREDENTIALS, 'INVALID_CREDENTIALS')
      }
      if (res.mfa_required === true) {
        if (typeof res.mfa_token !== 'string' || !res.mfa_token) {
          return withError(state, MESSAGES.UNEXPECTED_RESPONSE, null)
        }
        return {
          ...initialLoginFlowState,
          step: res.enrollment_required === true ? 'enroll' : 'code',
          mfaToken: res.mfa_token,
          errorSeq: state.errorSeq,
        }
      }
      // Legacy shape (ADMIN_TOTP_ENFORCED=false): {status:true, message, token}
      if (typeof res.token === 'string' && res.token) {
        return {...initialLoginFlowState, step: 'done', sessionToken: res.token, errorSeq: state.errorSeq}
      }
      return withError(state, MESSAGES.UNEXPECTED_RESPONSE, null)
    }

    case 'ENROLL_STARTED': {
      const res = action.response || {}
      if (state.step !== 'enroll') return state
      if (res.status !== true || typeof res.qr_data_url !== 'string' || typeof res.manual_key !== 'string') {
        return withError(state, MESSAGES.UNEXPECTED_RESPONSE, null)
      }
      return {
        ...state,
        enrollment: {
          otpauthUrl: typeof res.otpauth_url === 'string' ? res.otpauth_url : '',
          qrDataUrl: res.qr_data_url,
          manualKey: res.manual_key,
        },
        error: null,
        errorCode: null,
      }
    }

    case 'ENROLL_CONFIRMED': {
      const res = action.response || {}
      if (state.step !== 'enroll') return state
      const codes = Array.isArray(res.backup_codes) ? res.backup_codes.filter((c) => typeof c === 'string') : []
      if (res.status !== true || typeof res.token !== 'string' || !res.token || codes.length === 0) {
        return withError(state, MESSAGES.UNEXPECTED_RESPONSE, null)
      }
      return {
        ...state,
        step: 'backup-codes',
        mfaToken: null, // no longer needed; never send it anywhere again
        sessionToken: res.token, // held in memory until CODES_ACKNOWLEDGED
        backupCodes: codes,
        enrollment: null,
        error: null,
        errorCode: null,
        failedAttempts: 0,
      }
    }

    case 'VERIFIED': {
      const res = action.response || {}
      if (state.step !== 'code') return state
      if (res.status !== true || typeof res.token !== 'string' || !res.token) {
        return withError(state, MESSAGES.UNEXPECTED_RESPONSE, null)
      }
      return {
        ...state,
        step: 'done',
        mfaToken: null,
        sessionToken: res.token,
        error: null,
        errorCode: null,
        failedAttempts: 0,
      }
    }

    case 'CODES_ACKNOWLEDGED': {
      if (state.step !== 'backup-codes' || !state.sessionToken) return state
      return {...state, step: 'done', backupCodes: [], error: null, errorCode: null}
    }

    case 'REQUEST_FAILED': {
      const {httpStatus, code, message} = action.failure
      if (httpStatus === 429 || code === 'RATE_LIMITED') {
        return withError(state, MESSAGES.RATE_LIMITED, code || 'RATE_LIMITED')
      }
      if (httpStatus === null) {
        return withError(state, MESSAGES.NETWORK, null)
      }
      if (code && RETURN_TO_PASSWORD_CODES.has(code)) {
        const text = code === 'MFA_LOCKED' ? MESSAGES.MFA_LOCKED : MESSAGES.MFA_TOKEN_EXPIRED
        return {
          ...initialLoginFlowState,
          error: text,
          errorCode: code,
          errorSeq: state.errorSeq + 1,
        }
      }
      if (code === 'MFA_CODE_INVALID') {
        const failedAttempts = state.failedAttempts + 1
        return {
          ...withError(state, mfaCodeInvalidMessage(failedAttempts), code),
          failedAttempts,
        }
      }
      if (code === 'INVALID_CREDENTIALS') {
        return withError(state, MESSAGES.INVALID_CREDENTIALS, code)
      }
      return withError(state, message || MESSAGES.UNEXPECTED, code)
    }

    case 'BACK_TO_PASSWORD':
      return {...initialLoginFlowState, errorSeq: state.errorSeq}

    case 'CLEAR_ERROR':
      if (!state.error && !state.errorCode) return state
      return {...state, error: null, errorCode: null}

    default:
      return state
  }
}
